import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, GitGraph, MessageSquare, TrendingUp, Users, Brain, BarChart3, PieChart, Activity, Link2 } from 'lucide-react';
import { useData } from '../context/DataContext';

interface DashboardStats {
  total_documents: number;
  total_sections: number;
  total_content_length: number;
  avg_section_length: number;
  total_unique_entities: number;
  entity_breakdown: Record<string, number>;
  sections_per_document: Record<string, number>;
  relationship_strength: { strong: number; medium: number; weak: number };
  document_details: Record<string, { name: string; sections: number }>;
}

const Dashboard: React.FC = () => {
  const { sections, parsedData, chatHistory, error, selectedDocIds, sessionId, documents } = useData();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Get API URL (same logic as DataContext)
  const getApiBaseUrl = () => {
    const currentHost = window.location.hostname;
    if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
      return 'http://20.107.168.51:8002';
    }
    return `${window.location.protocol}//${currentHost}:8002`;
  };
  const API_BASE_URL = getApiBaseUrl();

  // Fetch dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!sections || sections.length === 0) return;
      
      setLoadingStats(true);
      try {
        const docIdsParam = selectedDocIds.length > 0 ? selectedDocIds.join(',') : '';
        const url = `${API_BASE_URL}/dashboard-stats?session_id=${sessionId}${docIdsParam ? `&doc_ids=${docIdsParam}` : ''}&_t=${Date.now()}`;
        const response = await fetch(url, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          },
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    
    fetchStats();
  }, [sections, selectedDocIds, sessionId, API_BASE_URL]);

  const totalSections = stats?.total_sections || sections?.length || 0;
  const totalChatMessages = chatHistory?.length || 0;
  const totalDocuments = stats?.total_documents || documents?.length || 0;
  
  // Fix: Properly handle entities to avoid React rendering errors
  const totalEntities = parsedData?.entities ? 
    Object.values(parsedData.entities).reduce((total: number, entityList: any) => {
      if (Array.isArray(entityList)) {
        return total + entityList.length;
      }
      return total;
    }, 0) : 0;
    
  const meaningfulSections = sections?.filter(section => 
    section.summary && section.summary.length > 50
  ).length || 0;

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // Helper function to safely render entity lists
  const renderEntityList = (entities: any) => {
    if (!Array.isArray(entities)) return null;
    
    return entities.slice(0, 5).map((entity: any, idx: number) => {
      // Handle both string entities and object entities
      const entityText = typeof entity === 'string' ? entity : 
                        (entity?.name || entity?.title || JSON.stringify(entity));
      return (
        <p key={idx} className="text-gray-300 text-sm truncate">
          • {entityText}
        </p>
      );
    });
  };

  return (
    <div className="space-y-8">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-5xl font-extrabold text-center text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-600"
      >
        Scinter Dashboard
      </motion.h1>

      <p className="text-center text-gray-300 text-lg max-w-3xl mx-auto">
        Get insights from your documents, explore knowledge graphs, and chat with AI.
      </p>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center"
        >
          <p className="text-red-400">Error: {error}</p>
        </motion.div>
      )}

      {!sections || sections.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="bg-gray-800 p-6 rounded-xl shadow-lg text-center text-gray-300 border border-gray-700"
        >
          <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-xl font-semibold mb-4">No document loaded yet.</p>
          <p>Please navigate to the "Upload Document" section to get started.</p>
        </motion.div>
      ) : (
        <>
          {/* Main KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div 
              className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/30 shadow-xl" 
              variants={cardVariants} 
              initial="hidden" 
              animate="visible"
            >
              <div className="flex items-center justify-between mb-4">
                <FileText className="h-10 w-10 text-purple-400" />
                <Activity className="h-5 w-5 text-purple-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1">Total Documents</h3>
              <p className="text-4xl font-bold text-white mb-2">{totalDocuments}</p>
              <p className="text-sm text-gray-400">Selected PDF files</p>
              {stats && (
                <div className="mt-3 pt-3 border-t border-purple-500/20">
                  <p className="text-xs text-gray-400">Total Sections: <span className="text-purple-300 font-semibold">{stats.total_sections}</span></p>
                </div>
              )}
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-6 border border-green-500/30 shadow-xl" 
              variants={cardVariants} 
              initial="hidden" 
              animate="visible" 
              transition={{ delay: 0.1 }}
            >
              <div className="flex items-center justify-between mb-4">
                <GitGraph className="h-10 w-10 text-green-400" />
                <TrendingUp className="h-5 w-5 text-green-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1">Content Volume</h3>
              <p className="text-4xl font-bold text-white mb-2">
                {stats ? `${(stats.total_content_length / 1000).toFixed(1)}K` : '0'}
              </p>
              <p className="text-sm text-gray-400">Characters processed</p>
              {stats && (
                <div className="mt-3 pt-3 border-t border-green-500/20">
                  <p className="text-xs text-gray-400">Avg/Section: <span className="text-green-300 font-semibold">{Math.round(stats.avg_section_length)}</span></p>
                </div>
              )}
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-6 border border-blue-500/30 shadow-xl" 
              variants={cardVariants} 
              initial="hidden" 
              animate="visible" 
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-4">
                <Users className="h-10 w-10 text-blue-400" />
                <Brain className="h-5 w-5 text-blue-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1">Unique Entities</h3>
              <p className="text-4xl font-bold text-white mb-2">{stats?.total_unique_entities || totalEntities}</p>
              <p className="text-sm text-gray-400">Extracted entities</p>
              {stats && Object.keys(stats.entity_breakdown).length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-500/20">
                  <p className="text-xs text-gray-400">
                    Top: <span className="text-blue-300 font-semibold">
                      {Object.entries(stats.entity_breakdown).sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A'}
                    </span>
                  </p>
                </div>
              )}
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-pink-600/20 to-rose-600/20 backdrop-blur-sm rounded-2xl p-6 border border-pink-500/30 shadow-xl" 
              variants={cardVariants} 
              initial="hidden" 
              animate="visible" 
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center justify-between mb-4">
                <MessageSquare className="h-10 w-10 text-pink-400" />
                <Link2 className="h-5 w-5 text-pink-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-200 mb-1">Chat Activity</h3>
              <p className="text-4xl font-bold text-white mb-2">{totalChatMessages}</p>
              <p className="text-sm text-gray-400">Conversation messages</p>
              {stats && stats.relationship_strength && (
                <div className="mt-3 pt-3 border-t border-pink-500/20">
                  <p className="text-xs text-gray-400">
                    Relationships: <span className="text-pink-300 font-semibold">
                      {stats.relationship_strength.strong + stats.relationship_strength.medium + stats.relationship_strength.weak}
                    </span>
                  </p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Charts and Visualizations */}
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Entity Breakdown Chart */}
              {Object.keys(stats.entity_breakdown).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 shadow-xl"
                >
                  <div className="flex items-center mb-4">
                    <PieChart className="h-6 w-6 text-purple-400 mr-2" />
                    <h3 className="text-xl font-bold text-white">Entity Distribution</h3>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(stats.entity_breakdown)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 8)
                      .map(([type, count], idx) => {
                        const percentage = (count / stats.total_unique_entities) * 100;
                        return (
                          <div key={type} className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-300 capitalize">{type}</span>
                              <span className="text-sm font-bold text-white">{count}</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ delay: 0.5 + idx * 0.1, duration: 0.5 }}
                                className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </motion.div>
              )}

              {/* Relationship Strength Chart */}
              {stats.relationship_strength && (stats.relationship_strength.strong + stats.relationship_strength.medium + stats.relationship_strength.weak > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 shadow-xl"
                >
                  <div className="flex items-center mb-4">
                    <BarChart3 className="h-6 w-6 text-green-400 mr-2" />
                    <h3 className="text-xl font-bold text-white">PDF Relationships</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 rounded bg-purple-500" />
                          <span className="text-sm font-medium text-gray-300">Strong</span>
                        </div>
                        <span className="text-lg font-bold text-white">{stats.relationship_strength.strong}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${(() => {
                              const total = stats.relationship_strength.strong + stats.relationship_strength.medium + stats.relationship_strength.weak;
                              return (stats.relationship_strength.strong / Math.max(1, total)) * 100;
                            })()}%` 
                          }}
                          transition={{ delay: 0.6, duration: 0.5 }}
                          className="h-full bg-gradient-to-r from-purple-500 to-purple-600"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 rounded bg-indigo-500" />
                          <span className="text-sm font-medium text-gray-300">Medium</span>
                        </div>
                        <span className="text-lg font-bold text-white">{stats.relationship_strength.medium}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${(() => {
                              const total = stats.relationship_strength.strong + stats.relationship_strength.medium + stats.relationship_strength.weak;
                              return (stats.relationship_strength.medium / Math.max(1, total)) * 100;
                            })()}%` 
                          }}
                          transition={{ delay: 0.7, duration: 0.5 }}
                          className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 rounded bg-indigo-300" />
                          <span className="text-sm font-medium text-gray-300">Weak</span>
                        </div>
                        <span className="text-lg font-bold text-white">{stats.relationship_strength.weak}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-3">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${(() => {
                              const total = stats.relationship_strength.strong + stats.relationship_strength.medium + stats.relationship_strength.weak;
                              return (stats.relationship_strength.weak / Math.max(1, total)) * 100;
                            })()}%` 
                          }}
                          transition={{ delay: 0.8, duration: 0.5 }}
                          className="h-full bg-gradient-to-r from-indigo-300 to-indigo-400"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Sections per Document */}
              {stats.sections_per_document && Object.keys(stats.sections_per_document).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 shadow-xl"
                >
                  <div className="flex items-center mb-4">
                    <BarChart3 className="h-6 w-6 text-blue-400 mr-2" />
                    <h3 className="text-xl font-bold text-white">Sections per Document</h3>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(stats.sections_per_document)
                      .sort(([,a], [,b]) => b - a)
                      .map(([docId, count], idx) => {
                        const docName = stats.document_details[docId]?.name || docId;
                        const maxSections = Math.max(...Object.values(stats.sections_per_document));
                        return (
                          <div key={docId} className="space-y-1">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-300 truncate flex-1 mr-2" title={docName}>
                                {docName.length > 30 ? docName.substring(0, 30) + '...' : docName}
                              </span>
                              <span className="text-sm font-bold text-white">{count}</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${(count / maxSections) * 100}%` }}
                                transition={{ delay: 0.7 + idx * 0.1, duration: 0.5 }}
                                className="h-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Section Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"
          >
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
              <FileText className="h-6 w-6 mr-2 text-blue-400" />
              Document Sections ({totalSections} total)
            </h3>
            
            {sections.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
                {sections.slice(0, 20).map((section, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.05 }}
                    className="bg-gray-700 p-4 rounded-lg border border-gray-600"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-white mb-2">
                          {section.title || `Section ${index + 1}`}
                        </h4>
                        {section.summary && (
                          <p className="text-gray-300 text-sm mb-2 line-clamp-2">
                            {section.summary}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-400">
                          <span>Pages: {section.page_range?.join('-') || 'N/A'}</span>
                          <span>Length: {section.content_length || 0} chars</span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {sections.length > 20 && (
                  <p className="text-gray-400 text-center py-4">
                    ... and {sections.length - 20} more sections
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No sections available</p>
            )}
          </motion.div>

          {/* Entity Overview - Fixed to handle object entities */}
          {parsedData?.entities && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"
            >
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <TrendingUp className="h-6 w-6 mr-2 text-green-400" />
                Extracted Entities
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(parsedData.entities).map(([type, entities], index) => {
                  const entityCount = Array.isArray(entities) ? entities.length : 0;
                  return (
                    <motion.div
                      key={type}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                      className="bg-gray-700 p-4 rounded-lg border border-gray-600"
                    >
                      <h4 className="text-lg font-semibold text-white mb-2 capitalize">
                        {type} ({entityCount})
                      </h4>
                      {Array.isArray(entities) && entities.length > 0 && (
                        <div className="space-y-1">
                          {renderEntityList(entities)}
                          {entities.length > 5 && (
                            <p className="text-gray-400 text-xs">
                              ... and {entities.length - 5} more
                            </p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Chat Overview */}
          {chatHistory.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700"
            >
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <MessageSquare className="h-6 w-6 mr-2 text-purple-400" />
                Recent Chat Activity
              </h3>
              
              <div className="space-y-3">
                {chatHistory.slice(-3).map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9 + index * 0.1 }}
                    className={`p-3 rounded-lg ${
                      message.role === 'user' 
                        ? 'bg-blue-600/20 border border-blue-500/30' 
                        : 'bg-gray-700 border border-gray-600'
                    }`}
                  >
                    <div className="flex items-start space-x-2">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        message.role === 'user' ? 'bg-blue-400' : 'bg-green-400'
                      }`}></div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-300 line-clamp-2">
                          {message.content}
                        </p>
                        {message.citations && message.citations.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            {message.citations.length} citation(s)
                          </p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
